/**
 * AuthContext · état d'authentification + flow login + auto-refresh.
 *
 * Au boot de l'app, on tente un refresh silencieux (cookie httpOnly).
 * - Si succès : on est connecté, on récupère /me, on planifie le prochain refresh.
 * - Si 401 : on reste anonyme, l'utilisateur ira sur /login.
 *
 * L'access token reste en mémoire (pas localStorage / sessionStorage).
 * Le cookie refresh est httpOnly, géré côté API.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { LoginInput, LoginResponse, MeResponse } from "@victor/api-client";
import { isLoginMfaChallenge } from "@victor/types";
import { identifyUser, resetAnalytics } from "../lib/analytics";
import {
  api,
  getAccessToken,
  onUnauthorized,
  setAccessToken,
} from "../lib/api";

type User = MeResponse["user"];
type CliScope = NonNullable<MeResponse["cli"]>;

type AuthStatus = "loading" | "anonymous" | "authenticated";

type AuthCtx = {
  status: AuthStatus;
  user: User | null;
  /** Rôles assignés au user courant (GER, PLA, AGT, RH, CLI). */
  roles: string[];
  /** S2 · Scope CLI si user est portail client donneur d'ordre. */
  cli: CliScope | null;
  /** True si le tenant doit signer son contrat avant d'accéder à l'espace. */
  contractSignatureRequired: boolean;
  /** True si le mandat SEPA reste à poser (1er mois payé CB) → gate /mandat. */
  mandateRequired: boolean;
  /** True si la démo onboarding obligatoire n'est pas validée → gate /demo-requise. */
  demoRequired: boolean;
  /** Activités de sécurité du tenant (pilote l'affichage des outils métier). Vide = tout. */
  activites: string[];
  login: (input: LoginInput) => Promise<LoginResponse>;
  completeMfa: (mfaToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthCtx | null>(null);

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [cli, setCli] = useState<CliScope | null>(null);
  const [contractSignatureRequired, setContractSignatureRequired] =
    useState(false);
  const [mandateRequired, setMandateRequired] = useState(false);
  const [demoRequired, setDemoRequired] = useState(false);
  const [activites, setActivites] = useState<string[]>([]);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Applique une réponse /me : user + roles + cli + flag signature. */
  const applyMe = useCallback((me: MeResponse) => {
    setUser(me.user);
    identifyUser(me.user);
    setRoles(me.roles ?? []);
    setCli(me.cli ?? null);
    setContractSignatureRequired(Boolean(me.contractSignatureRequired));
    setMandateRequired(Boolean(me.mandateRequired));
    setDemoRequired(Boolean(me.demoRequired));
    setActivites(me.activites ?? []);
  }, []);

  /** Reset de l'identité au logout / erreur. */
  const clearIdentity = useCallback(() => {
    setUser(null);
    resetAnalytics();
    setRoles([]);
    setCli(null);
    setContractSignatureRequired(false);
    setMandateRequired(false);
    setDemoRequired(false);
    setActivites([]);
  }, []);

  const scheduleRefresh = useCallback((accessExpiresIn: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    // Refresh 30s avant l'expiration de l'access token.
    const ms = Math.max(5_000, (accessExpiresIn - 30) * 1000);
    refreshTimer.current = setTimeout(() => {
      void doRefresh();
    }, ms);
  }, []);

  const doRefresh = useCallback(async () => {
    try {
      const r = await api.auth.refresh();
      setAccessToken(r.accessToken);
      const me = await api.auth.me();
      applyMe(me);
      setStatus("authenticated");
      scheduleRefresh(r.accessExpiresIn);
    } catch {
      setAccessToken(null);
      clearIdentity();
      setStatus("anonymous");
    }
  }, [scheduleRefresh, applyMe, clearIdentity]);

  // Boot : refresh silencieux + hook unauthorized
  useEffect(() => {
    void doRefresh();
    onUnauthorized(() => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      setAccessToken(null);
      clearIdentity();
      setStatus("anonymous");
    });
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [doRefresh]);

  const login = useCallback(
    async (input: LoginInput): Promise<LoginResponse> => {
      const res = await api.auth.login(input);
      if (isLoginMfaChallenge(res)) {
        return res;
      }
      // Succès direct : on récupère roles + cli via /me (LoginResponse
      // ne porte pas ces infos pour rester rétrocompatible).
      setAccessToken(res.accessToken);
      const me = await api.auth.me();
      applyMe(me);
      setStatus("authenticated");
      scheduleRefresh(res.accessExpiresIn);
      return res;
    },
    [scheduleRefresh, applyMe],
  );

  const completeMfa = useCallback(
    async (mfaToken: string, code: string): Promise<void> => {
      const res = await api.auth.completeMfa({ mfaToken, code });
      setAccessToken(res.accessToken);
      const me = await api.auth.me();
      applyMe(me);
      setStatus("authenticated");
      scheduleRefresh(res.accessExpiresIn);
    },
    [scheduleRefresh, applyMe],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* on continue même si l'appel échoue */
    }
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    setAccessToken(null);
    clearIdentity();
    setStatus("anonymous");
  }, [clearIdentity]);

  const refreshMe = useCallback(async () => {
    if (!getAccessToken()) return;
    const me = await api.auth.me();
    applyMe(me);
  }, [applyMe]);

  const value = useMemo<AuthCtx>(
    () => ({
      status,
      user,
      roles,
      cli,
      contractSignatureRequired,
      mandateRequired,
      demoRequired,
      activites,
      login,
      completeMfa,
      logout,
      refreshMe,
    }),
    [
      status,
      user,
      roles,
      cli,
      contractSignatureRequired,
      mandateRequired,
      demoRequired,
      activites,
      login,
      completeMfa,
      logout,
      refreshMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
